import {Plural, Trans} from '@lingui/macro';
import {keyBy} from 'lodash';
import React, {FC, ReactNode, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {flexCenter} from '../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';
import {ScrollDirectionsEnum} from '../../../srcCommon/scroll/scrollerTypes';
import {GridAreaItem} from '../../components/common/grid';
import {computeMultiSelectCheckboxState} from '../../components/common/table/helpers/tableHelpers';
import {StaticVirtualTable} from '../../components/common/table/staticVirtualTable';
import {TableEmptyState} from '../../components/common/table/tableEmptyState';
import {TableMultiSelectHeaderRow} from '../../components/common/table/tableMultiSelectHeaderRow';
import {VirtualTableRowRenderer} from '../../components/common/table/virtualTableBase';
import {AsyncMoreStatusesEnum} from '../../store/appTypes';
import {useFetchCircleRelationships} from '../fetchers/circles';
import {useFetchTeammateIds} from '../fetchers/teammates';
import {settingsHorizontalSpacing} from '../settingsCommonConstants';
import {CircleEditorTeammateDropdown} from './circleEditorTeammateDropdown';
import {circleEditorTeammateColumns} from './circleEditorTeammateTableConfig';
import {CircleEditorTeammateTableRow} from './circleEditorTeammateTableRow';

/*
 * Props.
 */

interface CircleEditorTeammateTableProps {
  circleId?: number;
  teammateIds: ReadonlyArray<number>;
  onSetTeammateIds: HandlerOf<ReadonlyArray<number>>;
}

/*
 * Styles.
 */

const StyledTableContainerDiv = styled.div`
  margin-left: -${settingsHorizontalSpacing}px;
  height: 100%;
  position: relative;
`;

const StyledEmptyContainerDiv = styled.div`
  display: grid;
  grid-template-areas: 'icon' 'title' 'dropdown';
  justify-items: center;
  grid-gap: 10px;
`;

const StyledEmptyIconDiv = styled.div`
  grid-area: icon;
  height: 70px;
  width: 70px;
  background-color: ${(p) => p.theme.greys.shade30};
  border-radius: 999px;

  ${flexCenter}
  justify-content: center;
`;

const StyledEmptyIcon = styled(Icon)`
  height: 40px;
  width: 40px;
`;

/*
 * Component.
 */

export const CircleEditorTeammateTable: FC<CircleEditorTeammateTableProps> = (props) => {
  const [selectedTeammateIds, setSelectedTeammateIds] = useState<ReadonlyArray<number>>([]);

  // Teammate list logic.
  const {teammates, loadMore, moreStatus} = useFetchTeammateIds(props.teammateIds, {
    suspense: true,
    keepPreviousData: true,
  });

  const teammateIds = useMemo(() => teammates.map((teammate) => teammate.id), [teammates]);
  const teammateMap = useMemo(() => keyBy(teammates, 'id'), [teammates]);

  // Relationships logic.
  const {relationships} = useFetchCircleRelationships(props.circleId ?? 0, {
    enabled: props.circleId !== undefined,
  });

  const teammateRelationshipById = useMemo(
    () => keyBy(relationships?.teammates, 'teammateId'),
    [relationships?.teammates],
  );

  // Teammate select logic.
  const isTeammateSelected = useCallback(
    (teammateId?: number): boolean => teammateId !== undefined && selectedTeammateIds.includes(teammateId),
    [selectedTeammateIds],
  );

  const onToggleSelectedTeammateId = useCallback(
    (teammateId: number) => {
      if (isTeammateSelected(teammateId)) {
        setSelectedTeammateIds(selectedTeammateIds.filter((id) => id !== teammateId));
        return;
      }

      setSelectedTeammateIds([teammateId, ...selectedTeammateIds]);
    },
    [isTeammateSelected, selectedTeammateIds],
  );

  const onMultiSelectHeaderClick = useCallback(
    (isSelectingAll: boolean) => {
      if (isSelectingAll) {
        setSelectedTeammateIds(props.teammateIds);
        return;
      }

      setSelectedTeammateIds([]);
    },
    [props.teammateIds],
  );

  // Renderers
  const renderRow: VirtualTableRowRenderer<number> = useCallback(
    (teammateId, options, siblingData) => (
      <CircleEditorTeammateTableRow
        teammate={teammateMap[teammateId]}
        options={options}
        isSelected={isTeammateSelected(teammateId)}
        onSelect={() => onToggleSelectedTeammateId(teammateId)}
        shouldRemoveBottomBorderRadius={isTeammateSelected(siblingData?.next)}
        shouldRemoveTopBorderRadius={isTeammateSelected(siblingData?.previous)}
        isInMultiSelectMode={selectedTeammateIds.length !== 0}
        addedAt={teammateRelationshipById[teammateId]?.addedAt}
      />
    ),
    [
      isTeammateSelected,
      onToggleSelectedTeammateId,
      teammateRelationshipById,
      selectedTeammateIds.length,
      teammateMap,
    ],
  );

  const onDeleteClick = useCallback(() => {
    props.onSetTeammateIds(props.teammateIds.filter((id) => !selectedTeammateIds.includes(id)));
    setSelectedTeammateIds([]);
  }, [props, selectedTeammateIds]);

  const renderMultiSelectHeader = useCallback(
    (onMultiSelectHeaderChange: HandlerOf<boolean>) =>
      renderHeader(onMultiSelectHeaderChange, selectedTeammateIds, props.teammateIds, onDeleteClick),
    [onDeleteClick, props.teammateIds, selectedTeammateIds],
  );

  const emptyTable = renderEmptyTable(props.teammateIds, props.onSetTeammateIds);

  return (
    <StyledTableContainerDiv>
      <StaticVirtualTable<number>
        columns={circleEditorTeammateColumns}
        items={teammateIds}
        hasData={moreStatus !== AsyncMoreStatusesEnum.LOADING && teammateIds.length > 0}
        moreStatuses={{
          [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
          [ScrollDirectionsEnum.BOTTOM]: moreStatus,
        }}
        onLoadMore={loadMore}
        renderRow={renderRow}
        shouldRenderEmptyState={moreStatus === AsyncMoreStatusesEnum.ALL_LOADED && teammateIds.length === 0}
        shouldAllowMultiSelect
        isInMultiSelectMode={selectedTeammateIds.length > 0}
        onMultiSelectHeaderChange={onMultiSelectHeaderClick}
        size={VisualSizesEnum.SMALL}
        renderMultiSelectHeader={renderMultiSelectHeader}
        renderEmpty={() => emptyTable}
      />
    </StyledTableContainerDiv>
  );
};

/*
 * Helpers.
 */

function renderHeader(
  onMultiSelectHeaderChange: HandlerOf<boolean>,
  selectedIds: ReadonlyArray<number>,
  allTeammateIds: ReadonlyArray<number>,
  onDeleteClick: Handler,
): ReactNode {
  const multiSelectStatus = computeMultiSelectCheckboxState(selectedIds.length, allTeammateIds.length);

  return (
    <TableMultiSelectHeaderRow
      multiSelectStatus={multiSelectStatus}
      onMultiSelectChange={onMultiSelectHeaderChange}
      renderTotalItemsLabel={() => (
        <Plural
          id="circle-editor-multi-select-label"
          value={selectedIds.length}
          one="1 member selected"
          other="# members selected"
        />
      )}
    >
      <Button variant={ButtonVariantsEnum.SECONDARY_DANGER} onClick={onDeleteClick}>
        <Trans id="circle-editor-remove-teammates">Remove</Trans>
      </Button>
    </TableMultiSelectHeaderRow>
  );
}
const EMPTY_OFFSET = 48 /* header height */ + 20; /* empty padding */

function renderEmptyTable(teammateIds: ReadonlyArray<number>, onChange: HandlerOf<ReadonlyArray<number>>) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <TableEmptyState headerOffset={EMPTY_OFFSET}>
          <StyledEmptyContainerDiv>
            <StyledEmptyIconDiv>
              <StyledEmptyIcon color={theme.greys.shade50} name="teammates" />
            </StyledEmptyIconDiv>
            <GridAreaItem area="title">
              <Trans id="circle-editor-teammates-empty-title">Group empty</Trans>
            </GridAreaItem>
            <GridAreaItem area="dropdown">
              <CircleEditorTeammateDropdown teammateIds={teammateIds} onChange={onChange} />
            </GridAreaItem>
          </StyledEmptyContainerDiv>
        </TableEmptyState>
      )}
    </UserThemeConsumer>
  );
}
